import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { WishlistService } from "@services/wishlistService";
import type { RootState } from "../store";

export interface ShortlistCar {
  id: string;
  make: string;
  name: string;
}

interface ShortlistState {
  items: ShortlistCar[];
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: ShortlistState = {
  items: [],
  isLoading: false,
  error: null,
  initialized: false,
};

function parseWishlistResponse(data: any): ShortlistCar[] {
  if (!data) return [];
  const arr = Array.isArray(data) ? data : data?.data ?? [];
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item: any) => ({
      id: item.slug || item.id,
      make: item.brand || item.make,
      name: item.model || item.name,
    }))
    .filter((c: ShortlistCar) => c.id);
}

export const fetchShortlist = createAsyncThunk(
  "shortlist/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await WishlistService.fetchUserWishlist();
      if (response.status === "success") {
        return parseWishlistResponse(response.data);
      }
      return rejectWithValue(response.message || "Failed to fetch shortlist");
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch shortlist");
    }
  }
);

export const addToShortlist = createAsyncThunk(
  "shortlist/add",
  async (car: ShortlistCar, { rejectWithValue }) => {
    try {
      const response = await WishlistService.addToWishlist({
        slug: car.id,
        productType: "CAR",
      });
      if (response.status === "success") {
        const parsed = parseWishlistResponse(response.updatedWishlist);
        return parsed.length > 0 ? parsed : null;
      }
      return rejectWithValue(response.message || "Failed to add to shortlist");
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to add to shortlist");
    }
  }
);

export const removeFromShortlist = createAsyncThunk(
  "shortlist/remove",
  async (car: ShortlistCar, { rejectWithValue }) => {
    try {
      const response = await WishlistService.removeFromWishlist({
        slug: car.id,
        productType: "CAR",
      });
      if (response.status === "success") {
        const parsed = parseWishlistResponse(response.updatedWishlist);
        return parsed.length > 0 ? parsed : null;
      }
      return rejectWithValue(
        response.message || "Failed to remove from shortlist"
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to remove from shortlist"
      );
    }
  }
);

const shortlistSlice = createSlice({
  name: "shortlist",
  initialState,
  reducers: {
    initializeShortlist(state, action: PayloadAction<ShortlistCar[]>) {
      state.items = action.payload;
      state.initialized = true;
      state.isLoading = false;
      state.error = null;
    },
    clearShortlist(state) {
      state.items = [];
      state.initialized = false;
      state.error = null;
    },
    optimisticAdd(state, action: PayloadAction<ShortlistCar>) {
      if (!state.items.some((item) => item.id === action.payload.id)) {
        state.items.push(action.payload);
      }
    },
    optimisticRemove(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShortlist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShortlist.fulfilled, (state, action) => {
        state.items = action.payload;
        state.isLoading = false;
        state.initialized = true;
        state.error = null;
      })
      .addCase(fetchShortlist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addToShortlist.fulfilled, (state, action) => {
        if (action.payload) {
          state.items = action.payload;
        }
      })
      .addCase(addToShortlist.rejected, (state, action) => {
        const car = action.meta.arg;
        state.items = state.items.filter((item) => item.id !== car.id);
        state.error = action.payload as string;
      })
      .addCase(removeFromShortlist.fulfilled, (state, action) => {
        if (action.payload) {
          state.items = action.payload;
        }
      })
      .addCase(removeFromShortlist.rejected, (state, action) => {
        const car = action.meta.arg;
        if (!state.items.some((item) => item.id === car.id)) {
          state.items.push(car);
        }
        state.error = action.payload as string;
      });
  },
});

export const {
  initializeShortlist,
  clearShortlist,
  optimisticAdd,
  optimisticRemove,
} = shortlistSlice.actions;

export const selectShortlistItems = (state: RootState) =>
  state.shortlist.items;
export const selectShortlistLoading = (state: RootState) =>
  state.shortlist.isLoading;
export const selectShortlistError = (state: RootState) =>
  state.shortlist.error;
export const selectShortlistInitialized = (state: RootState) =>
  state.shortlist.initialized;
export const selectIsCarShortlisted = (state: RootState, carId: string) =>
  state.shortlist.items.some((item) => item.id === carId);

export default shortlistSlice.reducer;
